import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, TrendingUp, ExternalLink } from 'lucide-react';
import { externalLinks } from '../mockData';
import HeroCarousel from '../components/HeroCarousel';
import TestimonialsCarousel from '../components/TestimonialsCarousel';

const Home = () => {
  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "name": "Hair Extensions Training Academy",
        "url": "https://hairextensionstraining.academy",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://hairextensionstraining.academy/courses?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "EducationalOrganization",
        "name": "Hair Extensions Training Academy",
        "url": "https://hairextensionstraining.academy",
        "logo": "https://customer-assets.emergentagent.com/job_a235b419-86b2-4667-9fd0-c39959dca4de/artifacts/he7jel3z_hair%20extensions%20training%20academy%20logo.png",
        "description": "CPD-accredited hair extensions and hair loss training courses in the UK. Over 4,100 students trained since 2006.",
        "foundingDate": "2006",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "GB"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+44-20-3051-4338",
          "contactType": "customer service"
        },
        "sameAs": [
          "https://youtube.com/@needahairmakeover",
          "https://www.tiktok.com/@needahairmakeover",
          "https://facebook.com/needahairmakeover2",
          "https://instagram.com/needahairmakeover"
        ]
      }
    ]
  };

  return (
    <div className="page-container">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(homeSchema)}</script>
      </Helmet>

      {/* Hero Carousel Section */}
      <section className="hero-carousel-section">
        <HeroCarousel />
      </section>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Hair Extensions Training Academy
          </h1>
          <p className="hero-subtitle">
            Globally Accredited Training Led by Award-Winning Expert Diane Shawe
          </p>
          <div className="hero-buttons">
            <Link to="/courses" className="btn-primary">
              View Courses <ArrowRight size={20} />
            </Link>
            <Link to="/contact" className="btn-secondary">
              Contact Us
            </Link>
          </div>

          <a
            href="https://www.virtualspeedyservices.uk/courses/hook-and-latch-entrepreneur-hair-extensions-course"
            target="_blank"
            rel="noopener noreferrer"
            className="promo-banner-link"
            data-testid="home-promo-99-banner"
            aria-label="Enrol in the £99 Hook and Latch Entrepreneur Hair Extensions Course"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_e0838653-edb4-4055-aeea-30513770ad02/artifacts/9lac2oe8_got%2099%20pounds%20and%206%20hours.jpeg"
              alt="Got £99 and 6 hours? Enrol today in the Hook and Latch Entrepreneur Hair Extensions Course"
              className="promo-banner-image"
              loading="lazy"
            />
          </a>

          <div className="bootcamp-card" data-testid="home-bootcamp-card">
            <span className="bootcamp-eyebrow">Limited Spaces • Small Group Training</span>
            <h2 className="bootcamp-headline">JOIN OUR 1 DAY TRAINING BOOTCAMP</h2>
            <p className="bootcamp-lead">
              Tired of expensive courses that don't pay off? For just <strong>£99</strong>, award-winning
              <strong> Diane Shawe</strong> will personally teach you her proprietary damage-free hair
              extension technique – <strong>No glue, No threads, No cornrows needed!</strong>
            </p>

            <ul className="bootcamp-list">
              <li><span className="bootcamp-check" aria-hidden="true">✓</span> Pre online theory course</li>
              <li><span className="bootcamp-check" aria-hidden="true">✓</span> Hands-on practice dummy head and live models</li>
              <li><span className="bootcamp-check" aria-hidden="true">✓</span> Certificate of Completion included</li>
              <li><span className="bootcamp-check" aria-hidden="true">✓</span> Beginner-friendly</li>
            </ul>

            <p className="bootcamp-urgency">
              Spaces are <strong>STRICTLY capped</strong> for small groups – reserve yours <strong>NOW</strong> before they're gone!
            </p>

            <div className="bootcamp-cta-row">
              <a
                href="https://www.virtualspeedyservices.uk/courses/hook-and-latch-entrepreneur-hair-extensions-course"
                target="_blank"
                rel="noopener noreferrer"
                className="bootcamp-cta bootcamp-cta-primary"
                data-testid="home-bootcamp-cta-99"
              >
                Book £99 Day Course
                <ArrowRight size={18} />
              </a>
              <a
                href="https://www.virtualspeedyservices.uk/courses/hook-and-latch-business-starter-hair-extensions-course"
                target="_blank"
                rel="noopener noreferrer"
                className="bootcamp-cta bootcamp-cta-secondary"
                data-testid="home-bootcamp-cta-139"
              >
                Book £139.99 Starter Business Course
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Video Section */}
      <section className="section-container video-showcase">
        <div className="content-wrapper">
          <div className="video-wrapper">
            <div className="responsive-video">
              <iframe
                src="https://www.youtube.com/embed/BNcyfQPphGU"
                title="Hair Extensions Training Academy Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Market Overview */}
      <section className="section-container">
        <div className="content-wrapper">
          <div className="text-center-content">
            <h2 className="section-title">Join The £10 Billion Global Hair Market</h2>
            <p className="section-description">
              The hair extensions and hair loss solutions industry is experiencing unprecedented growth.
              With over 4100 students trained since 2006, our globally accredited courses provide the
              skills and certification you need to succeed in this thriving market.
            </p>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <Award className="stat-icon" size={40} />
              <h3 className="stat-number">4100+</h3>
              <p className="stat-label">Students Trained</p>
            </div>
            <div className="stat-card">
              <TrendingUp className="stat-icon" size={40} />
              <h3 className="stat-number">£10B</h3>
              <p className="stat-label">Global Market Size</p>
            </div>
            <div className="stat-card">
              <Users className="stat-icon" size={40} />
              <h3 className="stat-number">19 Years</h3>
              <p className="stat-label">Training Excellence</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Train With Us Section */}
      <section className="section-container why-train-section">
        <div className="content-wrapper">
          <h2 className="section-title">Why Train as a Hair Extension Professional?</h2>

          <div className="why-train-video" data-testid="why-train-video">
            <video
              src="https://customer-assets.emergentagent.com/job_e0838653-edb4-4055-aeea-30513770ad02/artifacts/07shpobw_students%20trainingnhai%20extensions%201.mp4"
              controls
              playsInline
              preload="metadata"
              className="why-train-video-player"
            >
              Your browser does not support the video tag.
            </video>
          </div>
          
          <div className="why-train-content">
            <div className="why-train-block">
              <h3 className="why-train-heading">Fast Track into a Profitable Industry</h3>
              <p className="why-train-text">
                Training as a hair extension technician, Consultant, Trainer or Cranial Prosthesis Consultant is the fastest way to enter one of the most profitable areas of the beauty industry. By learning 3–12 professional techniques, you can begin working with clients almost immediately, offering services that deliver instant results and high client satisfaction. Technician training focuses on safe application, hair health, blending, and aftercare, giving you the confidence to work on real clients from day one.
              </p>
            </div>

            <a
              href="https://www.qualiquiz.app/quiz/which-hair-extension-course-is-right-for-you-take-60-second-assessment"
              target="_blank"
              rel="noopener noreferrer"
              className="why-train-quiz-banner"
              data-testid="home-quiz-banner"
              aria-label="Take the 60-second career assessment: Which hair extension technique should you learn first?"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_e0838653-edb4-4055-aeea-30513770ad02/artifacts/fqz9jszw_WhatsApp%20Image%202026-06-30%20at%2017.49.06%20%284%29.jpeg"
                alt="Which hair extension technique should you learn first? Take the 60-second career assessment."
                className="why-train-quiz-image"
                loading="lazy"
              />
            </a>

            <div className="why-train-block">
              <h3 className="why-train-heading">Ideal for All Levels</h3>
              <p className="why-train-text">
                This route is ideal for beginners, mobile technicians, or beauty professionals looking to add a high-value service without committing to long training periods. With demand for extensions continuing to grow, qualified technicians are needed in salons, on-set, and for private clients. A technician qualification allows you to start earning quickly, build experience, and progress to advanced consultant or trainer levels when ready.
              </p>
            </div>

            <div className="why-train-image-wrapper" data-testid="why-train-consultant-image">
              <img
                src="https://customer-assets.emergentagent.com/job_e0838653-edb4-4055-aeea-30513770ad02/artifacts/tjqm0psb_WhatsApp%20Image%202026-06-30%20at%2014.45.20%20%282%29.jpeg"
                alt="Hair Extensions Training Academy consultants — There's more to adding hair!"
                className="why-train-image"
                loading="lazy"
              />
            </div>

            <div className="why-train-block">
              <h3 className="why-train-heading">Become a Trusted Consultant</h3>
              <p className="why-train-text">
                As a consultant, you're trained to assess hair health, recommend suitable solutions, and deliver bespoke results rather than one-size-fits-all applications. This level of qualification opens doors to higher pricing, repeat clientele, and professional recognition. Consultants are trusted specialists, often working with salons, private clients, and media professionals. With hair loss and thinning on the rise, clients are actively seeking qualified consultants who can offer safe, long-term solutions. This training is ideal for those serious about building a sustainable, respected career.
              </p>
            </div>

            <div className="why-train-block">
              <h3 className="why-train-heading">Hair Prosthesis Training</h3>
              <p className="why-train-text">
                Hair prosthesis training takes your skills beyond extensions into life-changing solutions. As a Hair Prosthesis Consultant, you learn how to measure, design, fit, and maintain wigs, toppers, toupees, and bespoke hair systems. This work supports insurance reports, clients experiencing hair loss from medical conditions, genetics, or trauma. This training teaches you how to compile detailed 12-month reports required by insurance companies, NHS referrals, and charitable funding bodies.
              </p>
            </div>

            <div className="why-train-block">
              <h3 className="why-train-heading">Opportunities in Fashion, Film, Theatre & Media</h3>
              <p className="why-train-text">
                The opportunity to work in Fashion, Film, Theatre & Media is real. Hair professionals trained in extensions and hair systems are in constant demand across fashion, film, theatre, television, and photography.
              </p>
            </div>

            <div className="why-train-shorts-wrapper" data-testid="why-train-shorts-video">
              <div className="why-train-shorts-frame">
                <iframe
                  src="https://www.youtube.com/embed/c-EC3OyCrZI"
                  title="Hair Extensions Training Academy — Growing Market Demand"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>

            <div className="why-train-block highlight-block">
              <h3 className="why-train-heading">A Growing Market with Real Demand</h3>
              <p className="why-train-text">
                In the UK alone, over 15 million people experience hair loss, according to the NHS—and the number is rising. Despite this, there are not enough qualified technicians and consultants to meet demand. This gap presents a major opportunity for trained professionals.
              </p>
            </div>

            <div className="why-train-block">
              <h3 className="why-train-heading">Your Path to Business Ownership</h3>
              <p className="why-train-text">
                Training opens the door to business ownership. Many graduates go on to open specialist salons, private consulting practices, or join franchise opportunities. With the right skills, systems, and support, you can build a business focused on high-value services rather than high volume. Specialist salons attract loyal clients, premium pricing, and long-term sustainability.
              </p>
              <p className="why-train-text why-train-conclusion">
                Whether you want independence or the backing of a proven model, training is the first step toward business freedom.
              </p>
            </div>

            <div className="why-train-video-wrapper" data-testid="why-train-business-video">
              <div className="responsive-video">
                <iframe
                  src="https://www.youtube.com/embed/ayW25NlNm_8"
                  title="Hair Extensions Training Academy — Your Path to Business Ownership"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Links */}
      <section className="section-container section-alt">
        <div className="content-wrapper">
          <h2 className="section-title text-center-content">Explore Our Network</h2>
          <div className="featured-links-grid">
            <a
              href={externalLinks.franchise}
              target="_blank"
              rel="noopener noreferrer"
              className="featured-link-card"
            >
              <div className="featured-link-content">
                <h3 className="featured-link-title">Franchise Opportunities</h3>
                <p className="featured-link-description">
                  Start your own hair extensions franchise with our proven business model
                </p>
                <span className="featured-link-btn">
                  Learn More <ExternalLink size={16} />
                </span>
              </div>
            </a>

            <a
              href={externalLinks.salon}
              target="_blank"
              rel="noopener noreferrer"
              className="featured-link-card"
            >
              <div className="featured-link-content">
                <h3 className="featured-link-title">Book Salon Appointment</h3>
                <p className="featured-link-description">
                  Experience award-winning hair services at our salon
                </p>
                <span className="featured-link-btn">
                  Book Now <ExternalLink size={16} />
                </span>
              </div>
            </a>

            <a
              href={externalLinks.store}
              target="_blank"
              rel="noopener noreferrer"
              className="featured-link-card"
            >
              <div className="featured-link-content">
                <h3 className="featured-link-title">Online Store</h3>
                <p className="featured-link-description">
                  Shop premium hair extension products and accessories
                </p>
                <span className="featured-link-btn">
                  Shop Now <ExternalLink size={16} />
                </span>
              </div>
            </a>

            <a
              href={externalLinks.blog}
              target="_blank"
              rel="noopener noreferrer"
              className="featured-link-card"
            >
              <div className="featured-link-content">
                <h3 className="featured-link-title">Blog & Resources</h3>
                <p className="featured-link-description">
                  Read industry insights, tips, and success stories
                </p>
                <span className="featured-link-btn">
                  Read More <ExternalLink size={16} />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Carousel Section */}
      <section className="section-container testimonials-section">
        <div className="content-wrapper">
          <TestimonialsCarousel />
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-container cta-section">
        <div className="content-wrapper">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Start Your Journey?</h2>
            <p className="cta-description">
              Join thousands of successful graduates and transform your career with globally accredited training
            </p>
            <Link to="/courses" className="btn-primary-large">
              Browse Our Courses <ArrowRight size={24} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;